import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import TableComponent from "../../Global Functions/tableComponentsCp";
import { getFranchiseSiteVisitList } from "../../Redux/Actions/siteVisitActions";
import { EllipsisOutlined } from "@ant-design/icons";
import DateRangePopup from "../../Utils/dateFilterPopup";
import { Badge, Button, Dropdown, Menu, Popover, Tag } from "antd";
import { ToFirstCapital } from "../../Utils/helpers";
import { Link } from "react-router-dom";

const FranchiseSiteVisitList = () => {
    const dispatch = useDispatch();
    const [searchQuery, setSearchQuery] = useState("");
    const [isDatePopupVisible, setIsDatePopupVisible] = useState(false);
    const [dateRange, setDateRange] = useState({ start: null, end: null });
    const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);

    const { franchiseSiteVisitList, loading } = useSelector((state) => state.siteVisit);

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ["5", "10", "20", "50", "100"],
    });

    const fetchDetails = React.useCallback(
        async (page, limit, showLoader = true) => {
            const data = {
                site_visit_by: ["all"],
                page,
                limit,
                search: debouncedSearch,
                ...(dateRange.start && { start_date: dateRange.start.format("YYYY-MM-DD") }),
                ...(dateRange.end && { end_date: dateRange.end.format("YYYY-MM-DD") }),
            };
            try {
                await dispatch(getFranchiseSiteVisitList(data, showLoader));
                if (!showLoader) {
                    // toast.success("Referrals list refreshed");
                }
            } catch {
                if (!showLoader) {
                    // toast.error("Failed to refresh referrals list");
                }
            }
        },
        [dispatch, debouncedSearch, dateRange]
    );

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 500);

        return () => clearTimeout(handler);
    }, [searchQuery]);

    const handleDateRangeApply = (start, end) => {
        if (start === null && end === null) {
            setDateRange({ start: null, end: null });
        } else {
            setDateRange({ start, end });
        }
        setIsDatePopupVisible(false);
        setPagination((prev) => ({ ...prev, current: 1 }));
    };

    const dataSource = franchiseSiteVisitList?.data?.map((item, index) => ({
        key: item._id,
        index: index + 1,
        project_id: item.project.project_id,
        project_names: item.agent_projects?.length
            ? item.agent_projects.map((p) => p.project_name)
            : item.project?.project_name
                ? [item.project.project_name]
                : [],
        project_address: item.project.street_address,
        no_of_guest: item.no_of_guest,
        site_visit_by: ToFirstCapital(item.site_visit_by),
        ride_type: ToFirstCapital(item.ride_type),
        pickup_address: item.pickup_address ? item.pickup_address : "N/A",
        pickup_date_time: (() => {
            const dateStr =
                item.pickup_date_time || item.site_visit_date_time || null;
            if (!dateStr) return "N/A";
            const localTimeStr = dateStr.endsWith("Z")
                ? dateStr.slice(0, -1)
                : dateStr;
            return new Date(localTimeStr).toLocaleString("en-GB", { hour12: false });
        })(),
        user_name: item.user.name,
        user_email: item.user.email,
        created_at: item.created_at,
        user_contact: `${item.user.country_code} ${item.user.contact_number}`,
        site_visit_purpose: item.site_visit_purpose,
        is_round_trip: item.is_round_trip === true ? "Yes" : "No",
        cab_type: item.cab_type ? item.cab_type.name : "N/A",
        drop_address: item.drop_address,
        franchisy_name: item.franchisy_details?.name,
        franchisy_id: item.franchisy_id,
        status: item.status,
    }));

    const columns = [
        {
            title: "Sr No.",
            align: "center",
            render: (_, __, index) => index + 1,
        },
        {
            title: "Project Name",
            dataIndex: "project_names",
            key: "project_names",
            align: "center",
            render: (value) => {
                if (!value?.length) return "N/A";
                const [first, ...rest] = value;
                return (
                    <span>
                        {first}
                        {rest.length > 0 && (
                            <Popover
                                content={value.map((name, i) => (
                                    <div key={i}>{name}</div>
                                ))}
                            >
                                <Tag style={{ marginLeft: 6, cursor: "pointer" }}>
                                    +{rest.length}
                                </Tag>
                            </Popover>
                        )}
                    </span>
                );
            },
        },
        // {
        //     title: "Project Address",
        //     dataIndex: "project_address",
        //     key: "project_address",
        //     align: "center",
        //     ellipsis: true,
        //     render: (value) => value || "N/A",
        // },
        {
            title: "User Name",
            dataIndex: "user_name",
            key: "user_name",
            align: "center",
            render: (value) => value || "N/A",
        },
        {
            title: "Contact Number",
            dataIndex: "user_contact",
            key: "user_contact",
            align: "center",
            render: (value) => value || "N/A",
        },
        {
            title: "Franchise Name",
            key: "franchisy_name",
            align: "center",
            dataIndex: "franchisy_name",
            render: (value) => value || "N/A",
        },
        {
            title: "Site Visit By",
            key: "site_visit_by",
            align: "center",
            dataIndex: "site_visit_by",
            render: (value) => value || "N/A",
        },
        // {
        //     title: "Drop Address",
        //     key: "drop_address",
        //     align: "center",
        //     dataIndex: "drop_address",
        //     ellipsis: true,
        //     render: (value) => value || "N/A",
        // },
        // {
        //     title: "Site Visit Purpose",
        //     key: "site_visit_purpose",
        //     align: "center",
        //     dataIndex: "site_visit_purpose",
        //     render: (value) => value || "N/A",
        // },
        {
            title: "Created At",
            align: "center",
            dataIndex: "created_at",
            key: "created",
            render: (record) => (record ? new Date(record).toLocaleString() : "N/A"),
        },
        {
            title: "Status",
            dataIndex: "status",
            align: "center",
            key: "status",
            render: (status) => {
                if (!status) return "N/A";

                const getStatusColor = (status) => ({
                    requested: "orange",
                    completed: "green",
                    booked: "blue",
                    cancelled: "red"
                }[status] || "default");

                return (
                    <span
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "4px 8px",
                            border: "1px solid #d9d9d9",
                            borderRadius: "10px",
                        }}
                    >
                        <Badge
                            color={getStatusColor(status)}
                            text={ToFirstCapital(status)}
                        />
                    </span>
                );
            },
        },
        {
            title: "Action",
            align: "center",
            render: (_, record) => (
                <Dropdown
                    trigger={["click"]}
                    overlay={
                        <Menu>
                            <Link to={`/franchise-site-visit/${record.key}`}>
                                <Menu.Item key="view">View</Menu.Item>
                            </Link>
                        </Menu>
                    }
                >
                    <Button
                        shape="square"
                        icon={<EllipsisOutlined />}
                    />
                </Dropdown>
            ),
        },
    ];

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchDetails(pagination.current, pagination.pageSize);
        }, 150);

        return () => clearTimeout(timer);
    }, [pagination.current, pagination.pageSize, fetchDetails]);


    useEffect(() => {
        if (franchiseSiteVisitList?.total) {
            setPagination((prevData) => ({
                ...prevData,
                total: franchiseSiteVisitList.total,
            }));
        }
    }, [franchiseSiteVisitList]);

    const handleTableChange = (pagination) => {
        setPagination(pagination);
    };

    return (
        <>
            <TableComponent
                h3heading="Franchise Site Visit"
                pagination={pagination}
                dataSource={dataSource}
                onChange={handleTableChange}
                columns={columns}
                setState={setSearchQuery}
                onCalendarClick={() => setIsDatePopupVisible(true)}
                loading={loading}
            ></TableComponent>

            <DateRangePopup
                visible={isDatePopupVisible}
                initialStart={dateRange.start}
                initialEnd={dateRange.end}
                onCancel={() => setIsDatePopupVisible(false)}
                onOk={handleDateRangeApply}
            />
        </>
    );
};

export default FranchiseSiteVisitList;